import { registerStyles } from "../utils/registerStyles";
import React, { useEffect, useMemo, useState } from "react";
import TimestampExportPopup from "../popup/TimestampExportPopup";
import LoadingIndicator from "../components/LoadingIndicator";
import { Navigate } from "react-router-dom";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  where,
  query,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import {
  FaCheck,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaChevronDown,
} from "react-icons/fa";
import { FaFilePen } from "react-icons/fa6";
import { auth, db } from "../firebase";
import { useCompany } from "../contexts/CompanyContext";
import { useAuth } from "../contexts/AuthContext";
import { scheduledRows, compareSessions, timingNotes } from "../utils/timestampSchedule";
import { approvedPunchTime } from "../utils/payrollTime";
import { messagesForSession } from "../utils/timestampMessages";

const timestampCache = new Map();
const timestampPendingReads = new Map();
const timestampLastRange = new Map();
const timestampRangeKey = (key, range) => `${key}:${range.from}:${range.to}`;

function readTimestampData(key, range, uid) {
  const requestKey = timestampRangeKey(key, range);
  if (!timestampPendingReads.has(requestKey)) {
    const from = parseDay(range.from);
    const until = addDays(parseDay(range.to), 1);
    // Ta med angränsande dygn så nattpass kan paras över veckogränsen.
    const punchFrom = addDays(from, -1);
    const punchUntil = addDays(until, 1);
    const ref = (name) => collection(db, "users", uid, name);
    const dated = (name) => query(ref(name), where("date", ">=", dayKey(punchFrom)), where("date", "<", dayKey(until)));
    const promise = Promise.all([
      getDocs(query(ref("punchEvents"), where("createdAt", ">=", punchFrom), where("createdAt", "<", punchUntil))),
      getDocs(dated("shifts")),
      getDocs(query(ref("timestampMessages"), where("date", ">=", range.from), where("date", "<=", range.to))),
      getDocs(ref("employed")), getDocs(ref("employeeGroups")), getDoc(doc(db, "users", uid)),
    ]).then(([events, shifts, messages, employees, groups, owner]) => {
      const rows = (snapshot) => snapshot.docs.map((item) => ({ ...item.data(), id: item.id }));
      const data = {
        range, events: rows(events).map((item) => ({ ...item, createdAtDate: toDate(item.createdAt) })),
        shifts: rows(shifts), messages: rows(messages), employees: rows(employees),
        groups: rows(groups), ownerGroupId: owner.data()?.groupId || "",
      };
      timestampCache.set(requestKey, data);
      return data;
    }).finally(() => timestampPendingReads.delete(requestKey));
    timestampPendingReads.set(requestKey, promise);
  }
  return timestampPendingReads.get(requestKey);
}

const COLORS = {
  bg: "#f5f6fb",
  card: "#ffffff",
  text: "#252b42",
  muted: "#8891a8",
  border: "#ececf4",
  purple: "#6323ef",
  purpleSoft: "#f4f1ff",
  green: "#16c79a",
  greenSoft: "#eafaf5",
  red: "#e65353",
  redSoft: "#fff0f0",
};

function toDate(value) {
  if (!value) return null;

  if (typeof value?.toDate === "function") {
    return value.toDate();
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(date) {
  if (!date) return "–";

  return date.toLocaleDateString("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

function formatTime(date) {
  if (!date) return "–";

  return date.toLocaleTimeString("sv-SE", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDuration(milliseconds) {
  if (milliseconds == null || milliseconds < 0) return "–";

  const totalMinutes = Math.floor(milliseconds / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (!hours) return `${minutes} min`;
  return `${hours} h ${String(minutes).padStart(2, "0")} min`;
}

function formatDurationExact(milliseconds) {
  if (milliseconds == null || milliseconds < 0) return "–";
  const seconds = Math.floor(milliseconds / 1000);
  return [Math.floor(seconds / 3600), Math.floor((seconds % 3600) / 60), seconds % 60]
    .map((part) => String(part).padStart(2, "0"))
    .join(":");
}

function formatTimeRange(start, end) {
  return start || end ? `${formatTime(start)} – ${formatTime(end)}` : "–";
}

function dayKey(date) {
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function startOfWeek(date) {
  const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  result.setDate(result.getDate() - ((result.getDay() + 6) % 7));
  return result;
}

function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function parseDay(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return null;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
}

function dateLabel(date) {
  return date.toLocaleDateString("sv-SE", { day: "numeric", month: "long", year: "numeric" });
}

function weekNumber(date) {
  const thursday = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  thursday.setDate(thursday.getDate() + 3 - ((thursday.getDay() + 6) % 7));
  const yearStart = new Date(thursday.getFullYear(), 0, 1);
  return 1 + Math.floor((thursday - yearStart) / 86400000 / 7);
}

/*
  Skapar arbetspass av punchEvents.

  Exempel:
  08:00 in
  12:00 out

  blir en rad:
  In 08:00 / Ut 12:00 / Arbetad tid 4 h
*/
function buildSessions(events) {
  const sorted = [...events].sort((a, b) => {
    const aTime = a.createdAtDate?.getTime() || 0;
    const bTime = b.createdAtDate?.getTime() || 0;
    return aTime - bTime;
  });

  const openSessions = new Map();
  const sessions = [];

  sorted.forEach((event) => {
    const employeeId = event.employeeId || "unknown";
    if (!event.createdAtDate || !event.employeeId) return;
    const key = employeeId;

    if (event.type === "in") {
      // Om det redan finns ett öppet pass sparar vi det först.
      if (openSessions.has(key)) {
        sessions.push(openSessions.get(key));
      }

      openSessions.set(key, {
        id: event.id,
        eventIds: [event.id],
        employeeId,
        employeeName: event.employeeName || "Okänd medarbetare",
        date: event.createdAtDate,
        clockIn: event.createdAtDate,
        clockOut: null,
        actorUid: event.actorUid || "",
      });

      return;
    }

    if (event.type === "out") {
      const open = openSessions.get(key);

      if (open && event.createdAtDate - open.clockIn <= 24 * 60 * 60 * 1000) {
        sessions.push({
          ...open,
          eventIds: [...open.eventIds, event.id],
          clockOut: event.createdAtDate,
          duration:
            event.createdAtDate && open.clockIn
              ? event.createdAtDate.getTime() - open.clockIn.getTime()
              : null,
        });

        openSessions.delete(key);
      } else {
        // Utstämpling utan en matchande instämpling.
        sessions.push({
          id: event.id,
          eventIds: [event.id],
          employeeId,
          employeeName: event.employeeName || "Okänd medarbetare",
          date: event.createdAtDate,
          clockIn: null,
          clockOut: event.createdAtDate,
          duration: null,
          actorUid: event.actorUid || "",
        });
      }
    }
  });

  // Lägg även till personer som fortfarande är instämplade.
  openSessions.forEach((session) => {
    sessions.push(session);
  });

  return sessions.sort((a, b) => {
    const aTime =
      a.clockIn?.getTime() ||
      a.clockOut?.getTime() ||
      a.date?.getTime() ||
      0;

    const bTime =
      b.clockIn?.getTime() ||
      b.clockOut?.getTime() ||
      b.date?.getTime() ||
      0;

    return bTime - aTime;
  });
}

export default function TimestampPage() {
  const { timestampEnabled, timestampLoading, activeCompany } = useCompany();

  if (timestampLoading) return <LoadingIndicator message="Laddar företag..." fullPage />;
  if (!timestampEnabled) return <Navigate to="/dashboard" replace />;

  return <TimestampContent key={activeCompany.companyAccountUid} />;
}

function TimestampContent() {
  const { profile, currentUser } = useAuth();
  const { activeCompany, loading: companyLoading } = useCompany();

  const cacheKey = `${currentUser?.uid || ""}:${activeCompany?.companyAccountUid || ""}`;
  const cachedRange = timestampLastRange.get(cacheKey);
  const [events, setEvents] = useState([]);
  const [timestampMessages, setTimestampMessages] = useState({ companyUid: null, items: [], loading: true, error: "" });
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeeGroups, setEmployeeGroups] = useState([]);
  const [ownerGroupId, setOwnerGroupId] = useState("");
  const [groupError] = useState("");
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [scheduleError] = useState("");
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [range, setRange] = useState(() => {
    if (cachedRange) return cachedRange;
    const start = startOfWeek(new Date());
    return { from: dayKey(start), to: dayKey(addDays(start, 6)) };
  });
  const [draftRange, setDraftRange] = useState(range);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => startOfWeek(new Date()));
  const [pickingEnd, setPickingEnd] = useState(false);
  const [passFilter, setPassFilter] = useState("all");
  const [exportOpen, setExportOpen] = useState(false);
  const [viewMode, setViewMode] = useState("simple");
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [savingTime, setSavingTime] = useState(false);
  const [timeMessage, setTimeMessage] = useState("");
  const [manualTimeOpen, setManualTimeOpen] = useState(false);
  const [manualStartTime, setManualStartTime] = useState("");
  const [manualEndTime, setManualEndTime] = useState("");
  const [pendingPayrollSource, setPendingPayrollSource] = useState("");
  useEffect(() => { setTimeMessage(""); setPendingPayrollSource(""); setManualTimeOpen(false); }, [selectedSessionId]);

  function openManualTime() {
    const rows = comparedSessions.filter((row) => row.schedule?.id === selectedSession?.schedule?.id).sort((a, b) => a.clockIn - b.clockIn);
    setManualStartTime(rows[0]?.clockIn ? formatTime(rows[0].clockIn) : "");
    setManualEndTime(rows.at(-1)?.clockOut ? formatTime(rows.at(-1).clockOut) : "");
    setManualTimeOpen(true);
    setPendingPayrollSource("manual");
    setTimeMessage("");
  }

  async function approveTime(source, manualTimes) {
    const schedule = selectedSession?.schedule;
    if (!schedule || savingTime) return;
    setSavingTime(true);
    setTimeMessage("");
    try {
      const payrollTime = manualTimes ? { source: "manual", ...manualTimes } : source === "scheduled" ? { source } : approvedPunchTime(
        comparedSessions.filter((row) => row.schedule?.id === schedule.id)
      );
      const user = auth.currentUser;
      const savingEmployee = employees.find((employee) =>
        [employee.id, employee.authUid, employee.uid].includes(user?.uid) ||
        (user?.email && employee.email?.trim().toLowerCase() === user.email.trim().toLowerCase())
      );
      const profileData = profile?.employedData || profile?.userData || profile;
      const approvedByName = savingEmployee?.name ||
        [savingEmployee?.firstName, savingEmployee?.lastName].filter(Boolean).join(" ").trim() ||
        [profileData?.firstName, profileData?.lastName].filter(Boolean).join(" ").trim() ||
        profileData?.name || user?.displayName || user?.email || "Okänd användare";
      const shiftRef = doc(db, "users", activeCompany.companyAccountUid, "shifts", schedule.shiftId);
      const updatedShift = await runTransaction(db, async (transaction) => {
        const snapshot = await transaction.get(shiftRef);
        if (!snapshot.exists()) throw new Error("Schemapasset finns inte längre.");
        const shift = snapshot.data();
        const approvalRef = doc(db, "users", activeCompany.companyAccountUid, "payrollApprovals", `${shift.date.slice(0, 7)}_${schedule.employeeId}`);
        const approval = await transaction.get(approvalRef);
        if (approval.data()?.approved) throw new Error("Lönen är redan godkänd. Ta bort lönegodkännandet innan du ändrar tiden.");
        const saved = { ...payrollTime, scheduledStartTime: shift.startTime || "", scheduledEndTime: shift.endTime || "", approvedAt: new Date().toISOString(), approvedBy: user?.uid || "", approvedByName };
        if (shift.employees?.length) {
          if (!shift.employees.some((row) => row.employeeId === schedule.assignedEmployeeId)) throw new Error("Personen finns inte längre på passet.");
          transaction.update(shiftRef, { employees: shift.employees.map((row) => row.employeeId === schedule.assignedEmployeeId ? { ...row, payrollTime: saved } : row), updatedAt: serverTimestamp() });
        } else {
          if (shift.employeeId !== schedule.assignedEmployeeId) throw new Error("Personen finns inte längre på passet.");
          transaction.update(shiftRef, { payrollTime: saved, updatedAt: serverTimestamp() });
        }
        return { ...shift, id: schedule.shiftId, ...(shift.employees?.length
          ? { employees: shift.employees.map((row) => row.employeeId === schedule.assignedEmployeeId ? { ...row, payrollTime: saved } : row) }
          : { payrollTime: saved }) };
      });
      setShifts((previous) => previous.map((shift) => shift.id === updatedShift.id ? updatedShift : shift));
      // Överlappande datumintervall kan innehålla samma pass.
      for (const [key, data] of timestampCache) {
        if (key.startsWith(`${cacheKey}:`)) {
          data.shifts = data.shifts.map((shift) => shift.id === updatedShift.id ? updatedShift : shift);
        }
      }
      setTimeMessage("Sparat. Personens löneunderlag har uppdaterats.");
      setManualTimeOpen(false);
      setPendingPayrollSource("");
    } catch (error) {
      setTimeMessage(error.message || "Kunde inte spara tiden. Försök igen.");
    } finally {
      setSavingTime(false);
    }
  }


  useEffect(() => {
    const uid = activeCompany?.companyAccountUid;
    if (!uid) return;
    let cancelled = false;
    const apply = (data) => {
      if (cancelled) return;
      setEvents(data.events);
      setShifts(data.shifts);
      setEmployees(data.employees);
      setEmployeeGroups(data.groups);
      setOwnerGroupId(data.ownerGroupId);
      setTimestampMessages({ companyUid: uid, items: data.messages, loading: false, error: "" });
    };
    timestampLastRange.set(cacheKey, range);
    const existing = timestampCache.get(timestampRangeKey(cacheKey, range));
    setLoading(true);
    setError("");
    const data = existing
      ? Promise.resolve(existing) : readTimestampData(cacheKey, range, uid);
    data.then(apply).catch((error) => {
      if (cancelled) return;
      console.error("Kunde inte hämta tidstämplingar:", error);
      setEvents([]);
      setShifts([]);
      setError("Kunde inte hämta tidstämplingarna. Försök igen genom att ladda om sidan.");
      setTimestampMessages({ companyUid: uid, items: [], loading: false, error: "Kunde inte hämta meddelanden från appen." });
    }).finally(() => {
      if (cancelled) return;
      setLoading(false);
      setScheduleLoading(false);
      setEmployeesLoading(false);
      setGroupsLoading(false);
    });
    return () => { cancelled = true; };
  }, [cacheKey, activeCompany?.companyAccountUid, range.from, range.to]);

  const sessions = useMemo(() => buildSessions(events), [events]);

  const schedules = useMemo(() => scheduledRows(shifts, employees), [shifts, employees]);
  const comparedSessions = useMemo(() => compareSessions(sessions, schedules, now), [sessions, schedules, now]);
  const groupNamesByEmployeeId = useMemo(() => {
    const namesByGroupId = new Map(employeeGroups.map((group) => [group.id, group.name]));
    const result = new Map();
    employees.forEach((employee) => {
      const name = namesByGroupId.get(employee.groupId) || "–";
      [employee.id, employee.employeeId, employee.authUid, employee.uid, employee.email]
        .filter(Boolean).forEach((id) => result.set(id, name));
    });
    result.set(activeCompany.companyAccountUid, namesByGroupId.get(ownerGroupId) || "–");
    return result;
  }, [employees, employeeGroups, ownerGroupId, activeCompany.companyAccountUid]);
  const groupForSession = (session) => groupNamesByEmployeeId.get(session.employeeId)
    || groupNamesByEmployeeId.get(session.schedule?.employeeId) || "–";
  const todayKey = dayKey(now);
  const selectedSession = comparedSessions.find((session) => session.id === selectedSessionId) || null;
  const currentMessages = timestampMessages.companyUid === activeCompany?.companyAccountUid ? timestampMessages : { items: [], loading: true, error: "" };
  const selectedMessages = messagesForSession(currentMessages.items, selectedSession, employees);

  function showWeek(date) {
    const start = startOfWeek(date);
    const next = { from: dayKey(start), to: dayKey(addDays(start, 6)) };
    setRange(next);
    setDraftRange(next);
    setDatePickerOpen(false);
  }

  function pickCalendarDay(date) {
    const key = dayKey(date);
    if (!pickingEnd || key < draftRange.from) {
      setDraftRange({ from: key, to: "" });
      setPickingEnd(true);
    } else {
      setDraftRange((current) => ({ ...current, to: key }));
      setPickingEnd(false);
    }
  }

  useEffect(() => {
    if (!selectedSessionId) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setSelectedSessionId(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedSessionId]);

  const filteredSessions = useMemo(() => {
    const cleanSearch = search.trim().toLowerCase();
    const rangeFrom = parseDay(range.from);
    const rangeToExclusive = range.to ? addDays(parseDay(range.to), 1) : null;

    return comparedSessions.filter((session) => {
      const matchesSearch =
        !cleanSearch ||
        session.employeeName
          ?.toLowerCase()
          .includes(cleanSearch);

      const date = session.date || session.clockIn || session.clockOut;
      const matchesPass = passFilter === "all" ||
        (passFilter === "ongoing" && session.clockIn && !session.clockOut) ||
        (passFilter === "finished" && session.clockIn && session.clockOut) ||
        (passFilter === "missing" && session.missing) ||
        (passFilter === "planned" && session.planned);
      return matchesSearch && matchesPass && date >= rangeFrom && date < rangeToExclusive;
    });
  }, [comparedSessions, search, range.from, range.to, passFilter]);

  const groupedSessions = useMemo(() => {
    const groups = new Map();
    const from = parseDay(range.from);
    const to = parseDay(range.to);
    if (from && to) {
      for (let date = from; date <= to; date = addDays(date, 1)) {
        groups.set(dayKey(date), { date, sessions: [] });
      }
    }
    filteredSessions.forEach((session) => {
      const date = session.date || session.clockIn || session.clockOut;
      const key = dayKey(date);
      if (!groups.has(key)) groups.set(key, { date, sessions: [] });
      groups.get(key).sessions.push(session);
    });
    return [...groups.values()].sort((a, b) => a.date - b.date);
  }, [filteredSessions, range.from, range.to]);

  if (companyLoading || loading || scheduleLoading || employeesLoading || groupsLoading) {
    return <LoadingIndicator message="Laddar tidstämplingar..." fullPage />;
  }

  return (
    <div
      style={{
        minHeight: "100%",
        background: COLORS.bg,
        color: COLORS.text,
        fontFamily: "Inter, Arial, sans-serif",
      }}
    >
      <div style={{ marginBottom: 10, padding: "0 0 4px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <p style={{ color: "#6b7280", fontSize: 12, margin: 0 }}>
          Visar tidstämpling för: <strong>{activeCompany?.companyName || "Aktivt företag"}</strong>
        </p>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 14,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              color: COLORS.purple,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              marginBottom: 4,
            }}
          >
            Tidrapportering
          </div>

          <h1
            style={{
              margin: 0,
              fontSize: 26,
              fontWeight: 800,
              color: COLORS.text,
            }}
          >
            Tidstämpling
          </h1>

         
        </div>
        <button type="button" className="timestamp-export-button" disabled={Boolean(error)} onClick={() => setExportOpen(true)}>Exportera Skatteverket</button>
      </div>

      <div
        style={{
          background: COLORS.card,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 14,
          overflow: "visible",
          boxShadow: "0 8px 30px rgba(37, 43, 66, 0.04)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            padding: 14,
            borderBottom: `1px solid ${COLORS.border}`,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 15,
                fontWeight: 800,
                color: COLORS.text,
              }}
            >
              Stämplingar
            </div>

            <div
              style={{
                color: COLORS.muted,
                fontSize: 12,
                marginTop: 4,
              }}
            >
              {filteredSessions.length} arbetspass visas
            </div>
          </div>

          <div className="timestamp-controls">
            <input type="search" placeholder="Sök medarbetare..." aria-label="Sök medarbetare" value={search} onChange={(event) => setSearch(event.target.value)} style={inputStyle} />
            <button type="button" className="timestamp-week-button" aria-label="Föregående vecka" onClick={() => showWeek(addDays(parseDay(range.from), -7))}><FaChevronLeft /></button>
            <div className="timestamp-date-control">
              <button type="button" className="timestamp-date-trigger" aria-expanded={datePickerOpen} aria-controls="timestamp-date-picker" onClick={() => { setDraftRange(range); setCalendarMonth(parseDay(range.from)); setPickingEnd(false); setDatePickerOpen((open) => !open); }}>
                {range.from} – {range.to} <FaChevronDown aria-hidden="true" />
              </button>
              {datePickerOpen && (
                <div id="timestamp-date-picker" className="timestamp-date-popover">
                  <div className="timestamp-range-inputs">
                    <label>Från: <input type="date" value={draftRange.from} onChange={(event) => { setDraftRange((current) => ({ ...current, from: event.target.value })); setPickingEnd(true); }} /></label>
                    <label>Till: <input type="date" value={draftRange.to} onChange={(event) => { setDraftRange((current) => ({ ...current, to: event.target.value })); setPickingEnd(false); }} /></label>
                  </div>
                  <RangeCalendar month={calendarMonth} from={draftRange.from} to={draftRange.to} onMonthChange={setCalendarMonth} onPick={pickCalendarDay} />
                  <div className="timestamp-range-actions">
                    <button type="button" onClick={() => showWeek(now)}>Denna vecka</button>
                    <button type="button" onClick={() => setDatePickerOpen(false)}>Avbryt</button>
                    <button type="button" className="timestamp-apply-range" disabled={!parseDay(draftRange.from) || !parseDay(draftRange.to) || draftRange.from > draftRange.to} onClick={() => { setRange(draftRange); setDatePickerOpen(false); }}>Visa pass</button>
                  </div>
                </div>
              )}
            </div>
            <button type="button" className="timestamp-week-button" aria-label="Nästa vecka" onClick={() => showWeek(addDays(parseDay(range.from), 7))}><FaChevronRight /></button>
            <select className="timestamp-pass-filter" aria-label="Filtrera pass" value={passFilter} onChange={(event) => setPassFilter(event.target.value)}>
              <option value="all">Alla pass</option>
              <option value="ongoing">Pågående</option>
              <option value="finished">Avslutade</option>
              <option value="missing">Saknar stämpling</option>
              <option value="planned">Schemalagda</option>
            </select>
          </div>
        </div>

        <div className="timestamp-view-toolbar">
          <div className="timestamp-view-switch" role="group" aria-label="Visningsläge">
            <button type="button" className={viewMode === "simple" ? "active" : ""} aria-pressed={viewMode === "simple"} onClick={() => setViewMode("simple")}>Enkel</button>
            <button type="button" className={viewMode === "detailed" ? "active" : ""} aria-pressed={viewMode === "detailed"} onClick={() => setViewMode("detailed")}>Detaljerad</button>
          </div>
        </div>

        {(error || scheduleError || groupError) && (
          <div
            style={{
              margin: 18,
              padding: 14,
              borderRadius: 12,
              color: COLORS.red,
              background: COLORS.redSoft,
              fontWeight: 600,
            }}
          >
            {error || scheduleError || groupError}
          </div>
        )}

        {loading || scheduleLoading ? (
          <LoadingIndicator message="Hämtar tidstämplingar..." />
        ) : groupedSessions.length === 0 ? (
          <div
            style={{
              padding: 42,
              textAlign: "center",
              color: COLORS.muted,
            }}
          >
            Inga tidstämplingar hittades.
          </div>
        ) : (
          <div className="timestamp-table-wrap">
            <table className={`timestamp-table ${viewMode === "detailed" ? "timestamp-table-detailed" : ""}`}>
              <thead>{viewMode === "detailed" ? (
                <tr><th>Namn</th><th>Arbetad tid</th><th>Schemalagd tid</th><th>Stämplad tid</th><th>Rast</th><th>Grupp</th><th aria-label="Meddelande" /></tr>
              ) : (
                <tr><th>Namn</th><th>Status</th><th>Arbetad tid</th><th>Grupp</th><th aria-label="Meddelande" /></tr>
              )}</thead>
              <tbody>
                {groupedSessions.map((group) => (
                  <React.Fragment key={dayKey(group.date)}>
                    <tr className={`timestamp-date-row ${dayKey(group.date) > todayKey ? "timestamp-date-future" : ""}`}><th colSpan={viewMode === "detailed" ? 7 : 5}><strong>{dateLabel(group.date)}</strong><span>{group.date.toLocaleDateString("sv-SE", { weekday: "long" })}, vecka {weekNumber(group.date)}</span></th></tr>
                    {group.sessions.length === 0 && <tr className="timestamp-empty-day"><td colSpan={viewMode === "detailed" ? 7 : 5}>Inga pass</td></tr>}
                    {group.sessions.map((session) => {
                      const active = Boolean(session.clockIn) && !session.clockOut;
                      const status = session.planned ? "Schemalagd" : session.missing ? "Saknar stämpling" : active ? "Pågående" : "Avslutad";
                      return (
                        <tr key={`${session.id}-${session.clockOut?.getTime?.() || "open"}`} className="timestamp-session-row" onClick={() => setSelectedSessionId(session.id)}>
                          <td>
                            <button type="button" className="timestamp-row-button" onClick={() => setSelectedSessionId(session.id)} aria-label={`Visa rapporterad tid för ${session.employeeName}`}><strong>{session.employeeName}</strong></button>
                            {viewMode === "detailed" && <div className="timestamp-name-status"><span className={`timestamp-status ${active ? "timestamp-status-active" : session.missing ? "timestamp-status-missing" : session.planned ? "timestamp-status-planned" : ""}`}>{status}</span></div>}
                          </td>
                          {viewMode === "detailed" ? <>
                            <td>{formatTimeRange(session.clockIn, session.clockOut)}<span className="timestamp-detail-duration">{formatDurationExact(session.duration)}</span></td>
                            <td>{session.schedule ? formatTimeRange(session.schedule.start, session.schedule.end) : "–"}</td>
                            <td>{formatTimeRange(session.clockIn, session.clockOut)}</td>
                            <td>{session.schedule?.breakMs ? <>{formatDuration(session.schedule.breakMs)}<span className="timestamp-detail-duration">planerad</span></> : "–"}</td>
                            <td>{groupForSession(session)}</td>
                          </> : <>
                            <td><span className={`timestamp-status ${active ? "timestamp-status-active" : session.missing ? "timestamp-status-missing" : session.planned ? "timestamp-status-planned" : ""}`}>{status}</span></td>
                            <td>{session.planned ? formatTimeRange(session.schedule?.start, session.schedule?.end) : formatTimeRange(session.clockIn, session.clockOut)} <span className="timestamp-duration">{session.planned ? "planerad" : formatDuration(session.duration)}</span></td>
                            <td>{groupForSession(session)}</td>
                          </>}
                          <td className="timestamp-message-cell">
                            {messagesForSession(currentMessages.items, session, employees).length > 0 && (
                              <button type="button" className="timestamp-message-button" title="Visa sparat meddelande" aria-label={`Visa sparat meddelande för ${session.employeeName}`} onClick={(event) => { event.stopPropagation(); setSelectedSessionId(session.id); }}>
                                <FaFilePen aria-hidden="true" />
                              </button>
                            )}
                            {["scheduled", "punched", "manual"].includes(session.schedule?.payrollTime?.source) && (
                              <span className="timestamp-approved-icon" role="img" aria-label="Tid godkänd för lön" title="Tid godkänd för lön">
                                <FaCheck aria-hidden="true" />
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {exportOpen && <TimestampExportPopup companyName={activeCompany?.companyName || "Aktivt företag"} range={range}
        sessions={filteredSessions} groupForSession={groupForSession} onClose={() => setExportOpen(false)} />}
      {selectedSession && (
        <div className="timestamp-modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setSelectedSessionId(null); }}>
          <section className="timestamp-modal" role="dialog" aria-modal="true" aria-labelledby="timestamp-modal-title">
            <header className="timestamp-modal-header"><div><h2 id="timestamp-modal-title">Rapporterad tid</h2><p>{selectedSession.employeeName}</p></div><button type="button" className="timestamp-close" onClick={() => setSelectedSessionId(null)} aria-label="Stäng detaljer"><FaTimes /></button></header>
            <div className="timestamp-modal-body">
              <section className="timestamp-message">
                <h3>Meddelande</h3>
                {currentMessages.loading ? <p role="status">Hämtar meddelanden...</p>
                  : currentMessages.error ? <p role="alert">{currentMessages.error}</p>
                  : selectedMessages.length ? selectedMessages.map((item) => <p key={item.id}>{item.message}</p>)
                  : <p>Inget meddelande sparat för den här personen och dagen.</p>}
              </section>
              {selectedSession.schedule?.message && (
                <section className="timestamp-message">
                  <h3>Meddelande för passet</h3>
                  <p>{selectedSession.schedule.message}</p>
                </section>
              )}
              {selectedSession.schedule?.employeeMessage && (
                <section className="timestamp-message">
                  <h3>Meddelande till {selectedSession.employeeName}</h3>
                  <p>{selectedSession.schedule.employeeMessage}</p>
                </section>
              )}
              <h3>Avvikelser och händelser</h3>
              {timingNotes(selectedSession, now).split(" · ").map((note, index) => <div className="timestamp-event" key={index}><TimingNote note={note} session={selectedSession} /></div>)}
              <dl className="timestamp-detail-list">
                <dt>Datum</dt><dd>{formatDate(selectedSession.date || selectedSession.clockIn || selectedSession.clockOut)}</dd>
                <dt>Arbetad tid</dt><dd>{manualTimeOpen ? <div className="timestamp-manual-time">
                  <label>Stämpla in <input type="time" value={manualStartTime} onChange={(event) => setManualStartTime(event.target.value)} /></label>
                  <label>Stämpla ut <input type="time" value={manualEndTime} onChange={(event) => setManualEndTime(event.target.value)} /></label>
                </div> : <>{formatTime(selectedSession.clockIn)} – {formatTime(selectedSession.clockOut)}<br />{formatDuration(selectedSession.duration)}{(pendingPayrollSource === "manual" || selectedSession.schedule?.payrollTime?.source === "manual") && <div className="timestamp-manual-change-summary">Ändrad till {pendingPayrollSource === "manual" ? `${manualStartTime} – ${manualEndTime}` : `${selectedSession.schedule.payrollTime.startTime} – ${selectedSession.schedule.payrollTime.endTime}`}</div>}</>}</dd>
                <dt>Status</dt><dd>{selectedSession.planned ? "Schemalagd" : selectedSession.missing ? "Saknar stämpling" : selectedSession.clockIn && !selectedSession.clockOut ? "Pågående" : "Avslutad"}</dd>
                <dt>Schemalagt pass</dt><dd>{selectedSession.schedule ? `${formatTime(selectedSession.schedule.start)} – ${formatTime(selectedSession.schedule.end)}` : "–"}</dd>
                <dt>Planerad rast</dt><dd>{selectedSession.schedule ? formatDuration(selectedSession.schedule.breakMs) : "–"}</dd>
                <dt>Grupp</dt><dd>{groupForSession(selectedSession)}</dd>
              </dl>
              <div className="timestamp-payroll-choice">
                {selectedSession.schedule ? <>
                  <div className="timestamp-payroll-actions">
                    <button type="button" disabled={savingTime} aria-pressed={pendingPayrollSource === "scheduled" || (!pendingPayrollSource && selectedSession.schedule.payrollTime?.source === "scheduled")} onClick={() => { setPendingPayrollSource("scheduled"); setManualTimeOpen(false); }}>Godkänn schemalagd tid</button>
                    <button type="button" disabled={savingTime || selectedSession.shiftWorkedMs == null || selectedSession.missing || selectedSession.planned} aria-pressed={pendingPayrollSource === "punched" || (!pendingPayrollSource && selectedSession.schedule.payrollTime?.source === "punched")} onClick={() => { setPendingPayrollSource("punched"); setManualTimeOpen(false); }}>Godkänn stämplad tid</button>
                    <button type="button" disabled={savingTime || selectedSession.shiftWorkedMs == null || selectedSession.missing || selectedSession.planned} aria-pressed={pendingPayrollSource === "manual" || (!pendingPayrollSource && selectedSession.schedule.payrollTime?.source === "manual")} onClick={openManualTime}>Ändra manuellt stämplad tid</button>
                  </div>
                  {savingTime && <p role="status">Sparar...</p>}
                </> : <p>Koppla personen till ett schemapass innan tiden kan godkännas för lön.</p>}
                {timeMessage && <p role="status">{timeMessage}</p>}
                {selectedSession.schedule?.payrollTime?.approvedAt && (
                  <p>
                    Sparat av <strong>{selectedSession.schedule.payrollTime.approvedByName || "Okänd användare"}</strong>
                    {" · "}{formatDate(toDate(selectedSession.schedule.payrollTime.approvedAt))}
                    {" kl. "}{formatTime(toDate(selectedSession.schedule.payrollTime.approvedAt))}
                  </p>
                )}
              </div>
              {selectedSession.schedule && <div className="timestamp-payroll-save-row">
                <button type="button" className="timestamp-payroll-save" disabled={savingTime || !pendingPayrollSource || (pendingPayrollSource !== "scheduled" && (selectedSession.shiftWorkedMs == null || selectedSession.missing || selectedSession.planned)) || (pendingPayrollSource === "manual" && (!manualStartTime || !manualEndTime || manualStartTime === manualEndTime))} onClick={() => approveTime(pendingPayrollSource, pendingPayrollSource === "manual" ? { startTime: manualStartTime, endTime: manualEndTime } : undefined)}>{savingTime ? "Sparar..." : "Spara"}</button>
              </div>}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function RangeCalendar({ month, from, to, onMonthChange, onPick }) {
  const firstMonth = new Date(month.getFullYear(), month.getMonth(), 1);
  const daysInMonth = new Date(firstMonth.getFullYear(), firstMonth.getMonth() + 1, 0).getDate();
  const offset = (firstMonth.getDay() + 6) % 7;
  const cells = [...Array(offset).fill(null), ...Array.from({ length: daysInMonth }, (_, index) => index + 1)];
  return (
    <div className="timestamp-calendar">
      <div className="timestamp-calendar-nav">
        <button type="button" aria-label="Föregående månad" onClick={() => onMonthChange(new Date(firstMonth.getFullYear(), firstMonth.getMonth() - 1, 1))}><FaChevronLeft /></button>
        <span>{firstMonth.toLocaleDateString("sv-SE", { month: "long", year: "numeric" })}</span>
        <button type="button" aria-label="Nästa månad" onClick={() => onMonthChange(new Date(firstMonth.getFullYear(), firstMonth.getMonth() + 1, 1))}><FaChevronRight /></button>
      </div>
      <div className="timestamp-calendar-grid">
        {["Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön"].map((day) => <span key={day} className="timestamp-calendar-weekday">{day}</span>)}
        {cells.map((day, index) => {
          if (!day) return <span key={`blank-${index}`} />;
          const date = new Date(firstMonth.getFullYear(), firstMonth.getMonth(), day);
          const key = dayKey(date);
          return <button key={key} type="button" className={`timestamp-calendar-day ${key >= from && key <= to ? "in-range" : ""} ${key === from || key === to ? "range-edge" : ""}`} onClick={() => onPick(date)} aria-label={dateLabel(date)}>{day}</button>;
        })}
      </div>
    </div>
  );
}

function TimingNote({ note, session }) {
  const arrivalLate = note.endsWith(" sen");
  const arrivalEarly = note.endsWith(" tidig");
  const departureEarly = note.endsWith(" tidigt");
  const departureLate = note.endsWith(" efter sluttid");
  if (!session.schedule || !(arrivalLate || arrivalEarly || departureEarly || departureLate)) {
    return <div>{note}</div>;
  }

  const arrival = arrivalLate || arrivalEarly;
  const title = arrivalLate ? "Försenad" : arrivalEarly ? "Tidig instämpling" : departureEarly ? "Gick tidigt" : "Sen utstämpling";

  return (
    <div>
      <strong>{title}</strong>
      {` – schema ${arrival ? "" : "slutar "}${formatTime(arrival ? session.schedule.start : session.schedule.end)}, stämplar ${arrival ? "in" : "ut"} ${formatTime(arrival ? session.clockIn : session.clockOut)} → `}
      <span style={{
        display: "inline-block",
        padding: "3px 7px",
        borderRadius: 6,
        fontWeight: 700,
        color: COLORS.red,
        background: COLORS.redSoft,
      }}>
        {note}
      </span>
    </div>
  );
}

const inputStyle = {
  minHeight: 36,
  padding: "0 10px",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 12,
  outline: "none",
  background: "#fff",
  color: COLORS.text,
  fontSize: 12,
  fontFamily: "inherit",
};

// Styles live with this component; keep selectors and responsive rules together.
registerStyles("TimestampPage", `
.timestamp-controls { display: flex; align-items: center; flex-wrap: wrap; gap: 7px; font-size: 12px; }
.timestamp-week-button { min-height: 36px; padding: 0 10px; border: 1px solid #dce4f1; border-radius: 9px; background: #fff; color: #394353; cursor: pointer; font: inherit; font-weight: 650; }
.timestamp-week-button:hover { background: #f1f6ff; }
.timestamp-date-control { position: relative; }
.timestamp-date-trigger { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 36px; min-width: 225px; padding: 0 10px; border: 1px solid #dce4f1; border-radius: 9px; background: #fff; color: #394353; cursor: pointer; font: inherit; font-weight: 700; }
.timestamp-date-trigger:hover, .timestamp-date-trigger[aria-expanded="true"] { border-color: #77a8ee; background: #f8fbff; }
.timestamp-date-popover { position: absolute; top: calc(100% + 6px); right: 0; z-index: 20; width: min(320px, calc(100vw - 32px)); padding: 13px; border: 1px solid #dce4f1; border-radius: 10px; background: #fff; box-shadow: 0 16px 40px rgba(29, 50, 85, .16); }
.timestamp-range-inputs { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
.timestamp-range-inputs label { display: grid; gap: 5px; color: #718096; font-size: 12px; font-weight: 700; }
.timestamp-range-inputs input { box-sizing: border-box; width: 100%; min-width: 0; min-height: 36px; padding: 0 7px; border: 1px solid #dce4f1; border-radius: 7px; color: #394353; background: #fff; font: inherit; font-size: 13px; }
.timestamp-range-inputs input:focus { outline: 2px solid #b5d1fa; outline-offset: 1px; }
.timestamp-calendar-nav { display: flex; align-items: center; justify-content: space-between; margin: 16px 0 6px; color: #394353; font-size: 14px; font-weight: 750; text-transform: capitalize; }
.timestamp-calendar-nav button { display: grid; place-items: center; width: 30px; height: 30px; border: 0; border-radius: 7px; background: transparent; color: #557197; cursor: pointer; }
.timestamp-calendar-nav button:hover { background: #edf4ff; }
.timestamp-calendar-grid { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 2px; text-align: center; }
.timestamp-calendar-weekday { padding: 7px 0; color: #9aa9bd; font-size: 11px; font-weight: 700; }
.timestamp-calendar-day { display: grid; place-items: center; width: 100%; aspect-ratio: 1; max-height: 34px; border: 0; border-radius: 7px; background: transparent; color: #394353; cursor: pointer; font: inherit; font-size: 12px; }
.timestamp-calendar-day:hover { background: #e5f0ff; }
.timestamp-calendar-day.in-range { background: #eaf3ff; }
.timestamp-calendar-day.range-edge { background: #4f91ee; color: #fff; border-radius: 50%; font-weight: 700; }
.timestamp-range-actions { display: flex; justify-content: flex-end; gap: 6px; margin-top: 14px; padding-top: 12px; border-top: 1px solid #edf1f7; }
.timestamp-range-actions button { padding: 7px 8px; border: 0; border-radius: 7px; background: transparent; color: #536273; cursor: pointer; font: inherit; font-size: 12px; font-weight: 650; }
.timestamp-range-actions button:hover { background: #edf4ff; }
.timestamp-range-actions .timestamp-apply-range { padding-inline: 12px; background: #3f76bd; color: #fff; }
.timestamp-range-actions .timestamp-apply-range:hover { background: #3265a5; }
.timestamp-range-actions button:disabled { opacity: .5; cursor: not-allowed; }
.timestamp-pass-filter { min-height: 36px; min-width: 125px; padding: 0 8px; border: 1px solid #dce4f1; border-radius: 9px; background: #fff; color: #394353; font: inherit; font-weight: 700; }
.timestamp-view-toolbar { display: flex; justify-content: flex-end; padding: 7px 14px; border-bottom: 1px solid #e5ebf3; }
.timestamp-view-switch { display: inline-flex; gap: 3px; padding: 3px; border: 1px solid #dce4f1; border-radius: 9px; background: #f1f5fa; }
.timestamp-view-switch button { min-width: 78px; padding: 5px 9px; border: 0; border-radius: 6px; background: transparent; color: #536273; cursor: pointer; font: inherit; font-size: 13px; font-weight: 700; }
.timestamp-view-switch button.active { background: #fff; color: #2e68ae; box-shadow: 0 1px 5px rgba(29, 50, 85, .12); }
.timestamp-view-switch button:focus-visible { outline: 2px solid #4f91ee; outline-offset: 2px; }
.timestamp-table-wrap { overflow-x: auto; }
.timestamp-table { width: 100%; min-width: 620px; border-collapse: collapse; color: #394353; }
.timestamp-table-detailed { min-width: 860px; }
.timestamp-table thead { background: #f1f7ff; }
.timestamp-table th, .timestamp-table td { text-align: left; padding: 9px 15px; font-size: 13px; }
.timestamp-table thead th { font-weight: 700; }
.timestamp-table thead th:nth-child(1) { width: 23%; }
.timestamp-table thead th:nth-child(2) { width: 18%; }
.timestamp-table thead th:nth-child(3) { width: 41%; }
.timestamp-table-detailed thead th { width: auto !important; white-space: nowrap; }
.timestamp-table-detailed td { vertical-align: top; white-space: nowrap; }
.timestamp-table .timestamp-date-row th { padding-top: 17px; border-bottom: 1px solid #dfe7f2; }
.timestamp-date-row strong { font-size: 16px; }
.timestamp-date-row span { margin-left: 8px; font-weight: 400; color: #748095; }
.timestamp-date-future th, .timestamp-date-future span { color: #a5adbd; }
.timestamp-empty-day td { color: #a5adbd; font-size: 12px; border-bottom: 1px solid #e5ebf3; }
.timestamp-session-row { border-bottom: 1px solid #e5ebf3; cursor: pointer; }
.timestamp-session-row:hover, .timestamp-session-row:focus-within { background: #f7faff; }
.timestamp-row-button { padding: 0; border: 0; background: none; color: inherit; font: inherit; cursor: pointer; text-align: left; }
.timestamp-row-button:hover { text-decoration: underline; }
.timestamp-table td.timestamp-message-cell { width: 64px; text-align: right; white-space: nowrap; }
.timestamp-approved-icon { display: inline-flex; align-items: center; justify-content: center; margin-left: 6px; color: #168463; font-size: 16px; vertical-align: middle; }
.timestamp-message-button { display: inline-flex; align-items: center; justify-content: center; padding: 5px; border: 0; border-radius: 6px; background: transparent; color: #6323ef; font-size: 22px; cursor: pointer; vertical-align: middle; }
.timestamp-message-button:focus-visible { outline: 2px solid #6323ef; outline-offset: 2px; }
.timestamp-duration { margin-left: 14px; }
.timestamp-detail-duration { display: block; margin-top: 2px; color: #8290a2; font-size: 11px; }
.timestamp-name-status { margin-top: 4px; }
.timestamp-status { display: inline-block; padding: 3px 8px; border-radius: 999px; background: #e8eef7; color: #4b5666; font-weight: 650; font-size: 11px; }
.timestamp-status-active { background: #eafaf5; color: #128d70; }
.timestamp-status-missing { background: #fff0e8; color: #a65627; }
.timestamp-status-planned { background: #f1f3f7; color: #778297; }
.timestamp-modal-backdrop { position: fixed; inset: 0; z-index: 1000; display: flex; justify-content: center; align-items: center; padding: 20px; background: rgba(18, 28, 43, .55); }
.timestamp-modal { width: min(680px, 100%); max-height: min(90vh, 960px); overflow-y: auto; border-radius: 16px; background: #fff; color: #394353; box-shadow: 0 24px 70px rgba(0,0,0,.22); }
.timestamp-modal-header { display: flex; justify-content: space-between; align-items: flex-start; padding: 18px 22px; border-bottom: 1px solid #dfe7f2; }
.timestamp-modal-header h2 { margin: 0; font-size: 22px; }
.timestamp-modal-header p { margin: 4px 0 0; font-size: 17px; }
.timestamp-close { border: 0; background: none; font-size: 18px; color: #394353; cursor: pointer; }
.timestamp-modal-body { padding: 18px 22px 22px; font-size: 13px; }
.timestamp-modal-body h3 { margin: 0 0 10px; font-size: 18px; }
.timestamp-message { margin-bottom: 18px; padding: 14px; border: 1px solid #dce4f1; border-radius: 10px; background: #f8fbff; }
.timestamp-message p { margin: 0; white-space: pre-wrap; overflow-wrap: anywhere; line-height: 1.6; }
.timestamp-event { margin-bottom: 10px; padding: 14px; border: 1px solid #a6c9ff; border-radius: 10px; background: #f1f7ff; line-height: 1.6; }
.timestamp-detail-list { display: grid; grid-template-columns: 145px 1fr; gap: 8px 15px; margin: 18px 0; }
.timestamp-detail-list dt { font-weight: 700; }
.timestamp-detail-list dd { margin: 0; }
@media (max-width: 650px) { .timestamp-date-control { order: -1; flex-basis: 100%; } .timestamp-date-trigger { width: 100%; } .timestamp-date-popover { left: 0; right: auto; } .timestamp-detail-list { grid-template-columns: 1fr; gap: 4px; } .timestamp-detail-list dd { margin-bottom: 12px; } .timestamp-modal-header, .timestamp-modal-body { padding-left: 18px; padding-right: 18px; } }

.timestamp-payroll-choice { margin: 20px 0; padding: 16px; border: 1px solid #e4ddf5; border-radius: 10px; background: #faf8ff; }
.timestamp-payroll-choice p { font-size: 13px; line-height: 1.5; }
.timestamp-payroll-choice button { padding: 10px 12px; margin: 4px 8px 4px 0; border: 1px solid #6323ef; border-radius: 8px; color: #6323ef; background: white; cursor: pointer; }
.timestamp-payroll-choice button:disabled { opacity: .5; cursor: not-allowed; }

.timestamp-payroll-choice button[aria-pressed="true"] { background: #6323ef; color: #fff; box-shadow: 0 0 0 3px #e4ddf5; font-weight: 700; }
.timestamp-payroll-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 4px; }
.timestamp-payroll-save-row { display: flex; justify-content: flex-end; align-items: center; margin-top: 12px; }
.timestamp-payroll-save-row .timestamp-payroll-save { min-width: 128px; padding: 11px 22px; border: 0; border-radius: 10px; color: #fff; background: linear-gradient(135deg, #702eff, #5718dc); box-shadow: 0 4px 10px rgba(99, 35, 239, .22); font-size: 14px; font-weight: 700; transition: transform .15s ease, box-shadow .15s ease, opacity .15s ease; }
.timestamp-payroll-save-row .timestamp-payroll-save:not(:disabled):hover { transform: translateY(-1px); box-shadow: 0 6px 14px rgba(99, 35, 239, .3); }
.timestamp-payroll-save-row .timestamp-payroll-save:focus-visible { outline: 3px solid rgba(99, 35, 239, .25); outline-offset: 3px; }
.timestamp-payroll-save-row .timestamp-payroll-save:disabled { color: #9892a6; background: #e9e6ef; box-shadow: none; cursor: not-allowed; opacity: 1; }
.timestamp-manual-change-summary { margin-top: 4px; color: #6323ef; font-size: 13px; font-weight: 650; }
.timestamp-manual-time { display: flex; flex-wrap: wrap; align-items: end; gap: 8px; margin: 12px 0; }
.timestamp-manual-time label { display: grid; gap: 4px; color: #252b42; font-size: 13px; }
.timestamp-manual-time input { padding: 8px; border: 1px solid #c9c5d5; border-radius: 6px; font: inherit; }
`);
